import { type CitedSource, citedSourcesOf, readCitedSpans } from '@plato/shared';
import type { ReactNode } from 'react';

export function MessageParts({ parts }: { parts: readonly unknown[] }) {
  return (
    <div className="parts">
      {parts.map((part, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: parts have no ids of their own and never reorder.
        <Part key={index} part={asRecord(part)} />
      ))}
    </div>
  );
}

function Part({ part }: { part: Readonly<Record<string, unknown>> }) {
  const type = String(part.type ?? '');
  if (type === 'text') return <TextPart part={part} />;
  if (type === 'reasoning') {
    return (
      <details className="part-detail">
        <summary>Thinking</summary>
        <p className="part-text">{String(part.text ?? '')}</p>
      </details>
    );
  }
  if (type === 'file') {
    return (
      <p className="part-file">
        File: <code>{String(part.filename ?? part.mediaType ?? 'file')}</code>
      </p>
    );
  }
  if (type.startsWith('tool-') || type === 'dynamic-tool') {
    const name = type === 'dynamic-tool' ? String(part.toolName ?? 'tool') : type.slice(5);
    const failed = part.state === 'output-error';
    return (
      <details className="part-detail">
        <summary>
          Tool call <code>{name}</code>{' '}
          {failed ? <span className="chip chip-danger">failed</span> : null}
        </summary>
        <h4>Input</h4>
        <Json value={part.input} />
        {failed ? (
          <p role="note">{String(part.errorText ?? 'The call failed.')}</p>
        ) : (
          <>
            <h4>Output</h4>
            <Json value={part.output} />
          </>
        )}
      </details>
    );
  }
  if (type.startsWith('data-') || type === 'step-start' || type === 'source-url') return null;
  return (
    <details className="part-detail">
      <summary>
        <code>{type || 'part'}</code>
      </summary>
      <Json value={part} />
    </details>
  );
}

function TextPart({ part }: { part: Readonly<Record<string, unknown>> }) {
  const text = String(part.text ?? '');
  const spans = readCitedSpans(part);
  if (spans.length === 0) return <p className="part-text">{text}</p>;
  const sources = citedSourcesOf([part]);
  const numberOf = (source: CitedSource) =>
    sources.findIndex((known) => sourceKey(known) === sourceKey(source)) + 1;
  const pieces: ReactNode[] = [];
  let from = 0;
  for (const span of spans) {
    pieces.push(text.slice(from, span.end));
    const numbers = span.sources.map(numberOf);
    pieces.push(
      <sup key={`${span.start}:${span.end}`} className="part-cite">
        [{numbers.join(', ')}]
      </sup>,
    );
    from = span.end;
  }
  pieces.push(text.slice(from));
  return (
    <>
      <p className="part-text">{pieces}</p>
      <ol className="part-sources" aria-label="Sources">
        {sources.map((source) => (
          <li key={sourceKey(source)}>
            {source.url !== null ? (
              <a href={source.url} target="_blank" rel="noopener noreferrer">
                {source.title}
              </a>
            ) : (
              source.title
            )}
            {source.site !== null && source.site !== source.title ? (
              <span className="muted"> · {source.site}</span>
            ) : null}
            {source.quote !== null ? <q className="part-quote">{source.quote}</q> : null}
          </li>
        ))}
      </ol>
    </>
  );
}

function sourceKey(source: CitedSource): string {
  return source.url ?? `title:${source.title}`;
}

export function Json({ value }: { value: unknown }) {
  if (value === undefined) return <p className="muted">None.</p>;
  return (
    <pre className="json">{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
  );
}

function asRecord(value: unknown): Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}
